import { type JobStatus } from "./types/job";
import { type JobSummary } from "./types/job-summary";

/** Jobs per Dashboard page, the same in the card grid and the table so a
 * view switch keeps the page (#13). 12 fills 3 or 4 card columns evenly. */
export const DASHBOARD_PAGE_SIZE = 12;

export interface DashboardFilters {
  /** Matched against title and company, ignoring case. */
  search: string;
  status: "all" | JobStatus;
  /** A recruiter's jobs: the ones they own or are assigned to (GLOSSARY.md). */
  recruiter: "all" | string;
}

export interface DashboardJobs {
  jobs: JobSummary[];
  /** The page shown: the requested one, kept within 1…pageCount. */
  page: number;
  pageCount: number;
  /** Matching jobs, across all pages. */
  count: number;
  /** Candidates of all matching jobs, across all pages. */
  candidateCount: number;
}

// User ids come from Django as numbers despite the `string` type; the
// URL's recruiter is text. Compare them as text.
const sameUser = (id: string, other: string): boolean => String(id) === other;

function matches(job: JobSummary, filters: DashboardFilters, query: string): boolean {
  if (filters.status !== "all" && job.status !== filters.status) return false;
  if (
    filters.recruiter !== "all" &&
    !sameUser(job.createdBy, filters.recruiter) &&
    !job.assignedRecruiters.some((id) => sameUser(id, filters.recruiter))
  )
    return false;
  return !query || `${job.title} ${job.company}`.toLowerCase().includes(query);
}

/** Filters the Dashboard's job list and cuts out one page. The whole list
 * is loaded once, so filtering and paging happen here, not on the server
 * (#13). */
export function dashboardJobs(
  all: readonly JobSummary[],
  filters: DashboardFilters,
  requestedPage: number,
  pageSize: number = DASHBOARD_PAGE_SIZE,
): DashboardJobs {
  const query = filters.search.trim().toLowerCase();
  const matching = all.filter((job) => matches(job, filters, query));
  const pageCount = Math.max(1, Math.ceil(matching.length / pageSize));
  const page = Math.min(Math.max(1, Math.floor(requestedPage) || 1), pageCount);
  return {
    jobs: matching.slice((page - 1) * pageSize, page * pageSize),
    page,
    pageCount,
    count: matching.length,
    candidateCount: matching.reduce((sum, job) => sum + job.candidateCount, 0),
  };
}
